import {
  Box,
  IconButton,
  Button,
  Input,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AddIcon from "@mui/icons-material/Add";

import "react-toastify/dist/ReactToastify.css";
import Nodata from "../../../assets/errors/nodata.png"; // Adjust the path as necessary

const Modules = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    listing_Order: 0,
    module_name: "",
    categories: "",
    description: "",
    short_description: "",
    age_target_group: "",
    duration: "",
    total_hours: "",
    study_mode: "",
    delivery_time: "",
    price: "",
    image: "",
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getData("proccess.php", "modules_V2");
        if (response.success) {
          setTeams(response.data);
        } else {
          toast.error(response.message || "Failed to load module data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch modules");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  const handleAddClick = () => {
    setFormData({
      listing_Order: 0,
      module_name: "",
      categories: "",
      description: "",
      short_description: "",
      age_target_group: "",
      duration: "",
      total_hours: "",
      study_mode: "",
      delivery_time: "",
      price: "",
      image: "",
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (team) => {
    setSelectedTeam(team);
    setFormData({
      listing_Order: team.listing_Order || 0,
      module_name: team.module_name || "",
      categories: team.categories || "",
      description: team.description || "",
      short_description: team.short_description || "",
      age_target_group: team.age_target_group || "",
      duration: team.duration || "",
      total_hours: team.total_hours || "",
      study_mode: team.study_mode || "",
      delivery_time: team.delivery_time || "",
      price: team.price || "",
      image: team.image || "",
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (team) => {
    setTeamToDelete(team);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData(
        "proccess.php",
        "modules_V2",
        teamToDelete.id
      );
      if (response.success) {
        toast.success(`Module deleted successfully`);
        setTeams(teams.filter((team) => team.id !== teamToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete module");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete module");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setTeamToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      image: e.target.files[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
          data.append(key, formData[key]);
      });

      let response;
      if (selectedTeam) {

        response = await updateData(
          "proccess.php",
          "modules_V2",
          formData,
          selectedTeam.id
        );
        if (response.success) {
          toast.success(`Module updated successfully`);
          setTeams(response.data);
        }
      } else {
        response = await addData("proccess.php", "modules_V2", formData);
        if (response.success) {
          toast.success(`Module added successfully`);
          setTeams(response.data);
        }
      }

      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedTeam(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 80 },
    {field: 'listing_Order', headerName: 'Listing Order', flex: 0.5, minWidth: 80 },
    {
      field: "department",
      headerName: "Department",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box width={50} height={50}>
          {params.value || "STEAM"}
        </Box>
      ),
    },
    { field: "module_name", headerName: "Module Name", flex: 1, minWidth: 120 },
    { field: "categories", headerName: "Categories", flex: 1, minWidth: 150 },
    { field: "description", headerName: "Description", flex: 1, minWidth: 120 },
    {
      field: "short_description",
      headerName: "Short Description",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "age_target_group",
      headerName: "Age Target Group",
      flex: 1,
      minWidth: 120,
    },
    { field: "duration", headerName: "Duration", flex: 1, minWidth: 120 },
    { field: "total_hours", headerName: "Total Hours", flex: 1, minWidth: 120 },
    { field: "study_mode", headerName: "Study Mode", flex: 1, minWidth: 120 },
    { field: "price", headerName: "Price", flex: 1, minWidth: 120 },
    {
      field: "image",
      headerName: "Module Image",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box width={50} height={50}>
          {params.value ? (
            <img
              src={`https://mindsinaction.com.na/assets/media/pictures/${params.value}`}
              alt={params.value || "Module"}
              style={{ width: 50, height: 50, objectFit: "cover" }}
              onClick={() =>
                window.open(
                  `https://mindsinaction.com.na/assets/media/pictures/${params.value}`,
                  "_blank"
                )
              }
            />
          ) : (
            "No image"
          )}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No data available</Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ maxHeight: "100vh", m: "10px" }}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Dialog
        open={openEditDialog || openAddDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedTeam(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedTeam ? "Edit Module" : "Add New Module"}
        </DialogTitle>
        <DialogContent>
          <Form
            onSubmit={handleSubmit}
            style={{ width: "100%", padding: "1rem" }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="module_name"
                  name="module_name"
                  label="Module Name"
                  variant="standard"
                  value={formData.module_name}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  id="categories"
                  name="categories"
                  label="categories"
                  variant="standard"
                  value={formData.categories}
                  onChange={handleFormChange}
                  required
                  SelectProps={{ native: true }}
                >
                  <option key={""} value={""}></option>
                  <option key={"primary"} value={"primary"}>Primary School Learners</option>
                  <option key={"secondary"} value={"secondary"}>Secondary School Learners</option>
                  <option key={"tertiary"} value={"tertiary"}>Tertiary Students</option>
                  <option key={"outOfSchool"} value={"outOfSchool"}>Out Of School Youth</option>
                  <option key={"hobbyists"} value={"hobbyists"}>Hobbyists</option>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="description"
                  name="description"
                  label="Description"
                  multiline
                  minRows={2}
                  maxRows={8}
                  variant="standard"
                  value={formData.description}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="short_description"
                  name="short_description"
                  label="Short Description"
                  multiline
                  minRows={2}
                  maxRows={8}
                  variant="standard"
                  value={formData.short_description}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="age_target_group"
                  name="age_target_group"
                  label="Age Target Group"
                  variant="standard"
                  value={formData.age_target_group}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="listing_Order"
                  name="listing_Order"
                  label="Listing Order"
                  variant="standard"
                  value={formData.listing_Order}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="duration"
                  name="duration"
                  label="Duration"
                  variant="standard"
                  value={formData.duration}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="total_hours"
                  name="total_hours"
                  label="Total Hours"
                  variant="standard"
                  value={formData.total_hours}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="study_mode"
                  name="study_mode"
                  label="Study Mode"
                  variant="standard"
                  value={formData.study_mode}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="delivery_time"
                  name="delivery_time"
                  label="Delivery Time"
                  variant="standard"
                  value={formData.delivery_time}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="price"
                  name="price"
                  label="Price"
                  variant="standard"
                  value={formData.price}
                  onChange={handleFormChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <label>Module Image</label>
                <Input
                  type="file"
                  fullWidth
                  id="image"
                  name="image"
                  onChange={handleFileChange}
                  required={!selectedTeam}
                />
              </Grid>
            </Grid>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedTeam ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{teamToDelete?.module_name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        mb={"30px"}
        ml={"15px"}
        mr={"15px"}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{ mb: "5px" }}
          >
            Modules Management
          </Typography>
          <Typography variant="h5" color={colors.blue[400]}>
            Manage offered modules
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{
            backgroundColor: colors.blue[400],
            "&:hover": {
              backgroundColor: colors.blue[500],
            },
          }}
        >
          Add Module
        </Button>
      </Box>
      <Divider />
      <Box
        sx={{ height: "calc(100vh - 220px)", width: "100%" }}
        backgroundColor={colors.dark[300]}
        padding={".2rem"}
        mt={"1rem"}
      >
        <DataGrid
          rows={teams}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          sx={{
            border: "none",
            ".MuiDataGrid-cell": {
              borderBottom: "none",
              backgroundColor: "none",
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[900],
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Modules;
